/* eslint react/prop-types: 0 */
import styled, { ThemeContext } from 'styled-components';
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { legalPageRoutes, tabletRoutes } from '@utils/constants';
import { toggleLoginModal } from '@actions/auth';
import Drawer from '@base/Drawer/Drawer';
import H4 from '@base/H4/H4';
import P2 from '@base/P2/P2';
import H3 from '@base/H3/H3';
import P1 from '@base/P1/P1';
import Login from '@cont/Login/Login';
import Dialog from '@comp/Dialog/Dialog';
import { setShowReferral } from '@actions/player';
import { gotoReferralPage } from '@utils/referralHelper';
import { segment } from '@actions/analytics';
import TalkToUnacademy from '@cont/TalkToUnacademy/TalkToUnacademy';
import Button from '@base/Button/Button';
import Close from '@icon/Close';
import {
  REFERRAL_PLAYER_DIALOG,
  REFERRAL_PLAYER_POPUP,
  REFERRAL_TOUCHPOINT_CLOSED,
  TTU_FAB_CLICK,
  TTU_POP_UP_CLICK
} from '@constants/segment';
import imageHelper from '@utils/imageHelper';
import cookies from '@utils/cookies';
import { useLayoutOptionsSelector } from '@hooks/useLayoutOptions';
import ReferralImage from '@images/referrals/modal.svg';
import { showTTUCurtain } from '@actions/ttu';
import { Spacings } from '@styles/index';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { sendTTUEvents } from '@events/sendAnalytics';
import useWindowSize from '@hooks/useWindowSize';
import Header from './Header/Header';
import PlusNavBar from './NavBar/PlusNavBar';
import EduNavBar from './NavBar/EduNavBar';
import HelpCenterNavBar from './NavBar/HelpCenterNavBar';
import PageContent from './Components/Content';
import ParentContainer from './Components/AppContainer';
import BaseFooter from './Footer/BaseFooter/Footer';
import VideoModal from './VideoModal';
import LayoutContainer from './Components/Container';

const Container = styled(LayoutContainer)`
  @media (max-width: 768px) {
    min-width: 0px;
  }
  @media only screen and (max-width: 540px) {
    width: 100vw;
  }
`;

const AppContainer = styled(ParentContainer)`
  margin-top: ${({ transparentHeader }) => (transparentHeader ? '0' : '40px')};
  @media (max-width: 768px) {
    width: 100%;
  }
  @media (max-width: 47.95em) {
    margin-top: 0;
    display: grid;
    grid-column-gap: 8px;
    grid-row-gap: 0;
    grid-template-columns: repeat(10, 1fr);
  }
`;

const Content = styled(PageContent)`
  grid-column: ${({ fullWidth }) => (fullWidth ? 1 : 7)} / 25;
  grid-template-columns: repeat(
    ${({ fullWidth }) => (fullWidth ? 24 : 18)},
    32px
  );
  ${({ position }) => (position ? `position: ${position};` : '')}
  @media only screen and (max-width: 768px) {
    grid-template-columns: none;
    width: 100%;
  }
  @media only screen and (max-width: 47.95em) {
    grid-row-gap: 0;
    grid-column-gap: 8px;
    grid-column: 1/11;
    grid-template-columns: repeat(10, 1fr);
    padding: ${({ $noPadding }) => ($noPadding ? '0' : '0 16px')};
  }
`;

const TTUWidget = styled.div`
  background-color: ${({ theme }) => theme.colors.fontGrey};
  width: 64px;
  height: 64px;
  position: fixed;
  bottom: 24px;
  right: 24px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  @media (max-width: 47.95em) {
    display: none;
  }
`;

const TTUToolTip = styled.div`
  width: 360px;
  height: 212px;
  position: fixed;
  right: 24px;
  padding: 24px 16px;
  bottom: 105px;
  border-radius: 8px;
  background: ${({ theme }) =>
    `linear-gradient(0deg, ${theme.colors.whiteV4}, ${theme.colors.whiteV4})`};
  box-shadow: ${({ theme }) =>
    boxShadowGenerator({
      yOffset: Spacings.SPACING_1B,
      blur: Spacings.SPACING_4B,
      theme
    })};
`;

const StyledButton = styled(Button)`
  margin-top: 24px;
`;

const StyledP2 = styled(P2)`
  margin-top: 8px;
  color: ${({ theme }) => theme.colors.fontGrey};
`;

const TTUIllustration = styled.img`
  position: absolute;
  top: 16px;
  right: 0;
  height: 108px;
`;

const ReferralContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${Spacings.SPACING_10B} ${Spacings.SPACING_16B};
  text-align: center;
  max-width: 85%;
  margin: auto;
  img {
    margin-bottom: ${Spacings.SPACING_4B};
  }
  p {
    margin-top: ${Spacings.SPACING_3B};
    margin-bottom: ${Spacings.SPACING_6B};
  }
`;

const CloseReferral = styled(Close)`
  position: absolute;
  right: ${Spacings.SPACING_4B};
  top: ${Spacings.SPACING_4B};
  cursor: pointer;
`;

const subscribeUrl = '/goal/[goalSlug]/[goalUID]/subscribe';
const noPadding = [
  '/goal/[goalSlug]/[goalUID]',
  '/goal/[goalSlug]/[goalUID]/classes',
  '/lesson/[lessonSlug]/[lessonUID]',
  '/course/[slug]/[courseUID]',
  '/test-series/[slug]/[courseUID]',
  '/class/[slug]/[uid]',
  '/explore',
  '/manage-goals'
];

const Layout = ({ children, toggleDarkMode, isDarkModeOn, isEducatorSide }) => {
  const router = useRouter();
  const { route, query } = router;
  const isLoggedIn = useSelector(({ auth }) => auth.isLoggedIn);
  const me = useSelector(({ auth }) => auth.me);
  const selectedGoal = useSelector(({ auth }) => auth.selectedGoal);
  const showLoginModal = useSelector(({ auth }) => auth.showLoginModal);
  const { isMobile } = useWindowSize();
  const plusSubscriptionUids = useSelector(
    ({ auth }) => auth.me?.plusSubscriptionUids || [],
    shallowEqual
  );
  const ttuFlag = useSelector(
    ({ ttu }) =>
      (ttu.showData?.is_goal_enabled && ttu.showData?.state === 0) || false
  );
  const ttuShow = useSelector(({ ttu }) => ttu.showData?.showTTU || false);
  const isRepeatUser = useSelector(
    ({ ttu }) => ttu.showData?.is_repeat_user || false
  );
  const showReferral =
    useSelector(({ player }) => player.showReferralDialog) || false;
  const [showTTU, setShowTTU] = useState(ttuShow);
  const [showTTUTooltip, setShowTTUTooltip] = useState(false);
  const [ttuTriggerSource, setTTUTriggerSource] = useState('Scroll Trigger');
  const [showReferralDialog, setShowReferralDialog] = useState(showReferral);
  const theme = useContext(ThemeContext);

  const dispatch = useDispatch();

  const isSubscribed = plusSubscriptionUids.includes(
    query?.goalUID?.toUpperCase()
  );

  /**
   * To check whether it is called from domain other than unacademy
   */
  const isExternal = query?.external === '1';

  useEffect(() => {
    setShowReferralDialog(showReferral);
    if (showReferral) {
      segment.track(REFERRAL_PLAYER_POPUP);
    }
  }, [showReferral]);

  useEffect(() => {
    setShowTTU(ttuShow);
  }, [ttuShow]);
  const {
    hideIntercom = false,
    hideSearch: shouldHideSearch = false,
    isCheckoutUrl = false,
    hideFooter = false,
    hideHeader = false,
    useFreeLayout = false,
    showEducatorNav = false
  } = useLayoutOptionsSelector();

  let { showPlusNav } = useLayoutOptionsSelector();
  showPlusNav = isMobile ? false : showPlusNav;
  useEffect(() => {
    if (ttuFlag && !hideIntercom && !me.isEducator && !isMobile) {
      setTimeout(() => setShowTTUTooltip(true), 15000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userSubscribed = isLoggedIn && isSubscribed;
  const hideSearch = shouldHideSearch || isCheckoutUrl;
  const isLoginRoute = route === '/login';

  function onClose() {
    dispatch(toggleLoginModal(false));
  }

  function onCloseTTU() {
    setShowTTU(false);
    dispatch(showTTUCurtain(false));
  }

  function renderTTU() {
    return (
      <Drawer drawerState={showTTU} onClose={onCloseTTU}>
        <TalkToUnacademy onClose={onCloseTTU} />
      </Drawer>
    );
  }

  function renderTTUWidget() {
    if (ttuFlag && !hideIntercom && !me.isEducator) {
      return (
        <TTUWidget
          onClick={() => {
            if (!showTTUTooltip) {
              sendTTUEvents(
                TTU_FAB_CLICK,
                {
                  user_ttu_status: isRepeatUser ? 'Repeat' : 'New'
                },
                me?.uid,
                me?.username,
                selectedGoal?.uid,
                selectedGoal?.name
              );
              setTTUTriggerSource('FAB icon');
            }
            setShowTTUTooltip(!showTTUTooltip);
          }}
        >
          {showTTUTooltip ? (
            <Close color={theme.colors.base} size="24px" />
          ) : (
            <img
              src={imageHelper.getImage(
                isDarkModeOn ? 'ttu-dark.svg' : 'ttu.svg'
              )}
              alt="ttu"
            />
          )}
          {showTTUTooltip && (
            <TTUToolTip>
              <H4 color={theme.colors.fontGrey}>
                Have questions about the subscription?
              </H4>
              <StyledP2>Our expert can answer your questions</StyledP2>
              <StyledButton
                label="Talk to our expert"
                grid={5}
                onClick={() => {
                  setShowTTUTooltip(false);
                  setShowTTU(true);
                  sendTTUEvents(
                    TTU_POP_UP_CLICK,
                    {
                      user_ttu_status: isRepeatUser ? 'Repeat' : 'New',
                      last_primary_source: ttuTriggerSource
                    },
                    me?.uid,
                    me?.username,
                    selectedGoal?.uid,
                    selectedGoal?.name
                  );
                }}
              />
              <TTUIllustration
                src={imageHelper.getImage('ttu_illustration.svg')}
              />
            </TTUToolTip>
          )}
        </TTUWidget>
      );
    }
  }

  function renderLogin() {
    if (!isCheckoutUrl && !isLoginRoute) {
      return (
        <Drawer login drawerState={showLoginModal} onClose={onClose}>
          <Login />
        </Drawer>
      );
    }
  }

  const onReferralClose = () => {
    setShowReferralDialog(false);
    setShowReferral(null);
    cookies.setCookie('referral-player', new Date().getTime(), 604800);
    segment.track(REFERRAL_TOUCHPOINT_CLOSED, {
      last_primary_source: REFERRAL_PLAYER_DIALOG
    });
  };

  function renderReferralPopUp() {
    return (
      <Dialog onClose={onReferralClose} dialogState={showReferralDialog}>
        <ReferralContent>
          <CloseReferral size="24px" onClick={onReferralClose} />
          <img src={ReferralImage} alt="referral" />
          <H3 color={theme.colors.fontGrey}>Refer and Earn</H3>
          <P1 color={theme.colors.fontLightGrey}>
            Earn free subscription weeks by getting your friends to subscribe on
            Unacademy
          </P1>
          <Button
            label="Refer now"
            onClick={() => {
              setShowReferralDialog(false);
              setShowReferral(null);
              cookies.setCookie(
                'referral-player',
                new Date().getTime(),
                604800
              );
              gotoReferralPage(selectedGoal, REFERRAL_PLAYER_DIALOG, dispatch);
            }}
          />
        </ReferralContent>
      </Dialog>
    );
  }

  const insideApp = query.inside_app === 'true' || false;

  if (
    useFreeLayout ||
    (!userSubscribed && route === '/goal/[goalSlug]/[goalUID]')
  ) {
    return (
      <>
        {!hideHeader && !insideApp && (
          <Header
            transparent
            hideSearch={hideSearch}
            isCheckoutUrl={isCheckoutUrl}
            toggleDarkMode={toggleDarkMode}
            isDarkModeOn={isDarkModeOn}
          />
        )}
        {renderLogin()}
        {children}
        {!insideApp && (
          <BaseFooter display={!hideFooter} noFooter={hideFooter} />
        )}
        {isMobile ? null : renderTTU()}
        {isMobile ? null : renderTTUWidget()}
      </>
    );
  }

  const backgroundColor = legalPageRoutes.includes(route) ? '#fcfcfc' : null;
  const tabletWidth = tabletRoutes.includes(route) ? '728px' : '1136px';
  return isExternal ? (
    <Container backgroundColor="#01100D">
      {children}
      <VideoModal
        noDisplay={isEducatorSide || isMobile}
        toggleDarkMode={toggleDarkMode}
      />
    </Container>
  ) : (
    <Container backgroundColor={backgroundColor}>
      {!insideApp && !hideHeader && (
        <Header
          hideSearch={hideSearch}
          isCheckoutUrl={isCheckoutUrl}
          transparent={route === subscribeUrl}
          toggleDarkMode={toggleDarkMode}
          isDarkModeOn={isDarkModeOn}
          isLoginUrl={isLoginRoute}
        />
      )}
      <AppContainer
        backgroundColor={backgroundColor}
        transparentHeader={route === subscribeUrl}
        $tabletWidth={tabletWidth}
      >
        <PlusNavBar display={showPlusNav} />
        <EduNavBar display={showEducatorNav} />
        <HelpCenterNavBar
          display={route.indexOf('/helpcenter/category/[categoryID]') > -1}
        />
        <Content
          fullWidth={
            !showPlusNav &&
            !showEducatorNav &&
            route.indexOf('/helpcenter/category/[categoryID]') === -1
          }
          position={
            route.indexOf('/educator/drive/folder/[itemUID]') !== -1
              ? 'relative'
              : ''
          }
          $noPadding={noPadding.includes(route)}
        >
          {children}
        </Content>
        <VideoModal
          noDisplay={isEducatorSide || isMobile}
          toggleDarkMode={toggleDarkMode}
        />
      </AppContainer>
      {!insideApp && (
        <BaseFooter
          display={!hideFooter && !showPlusNav && !isEducatorSide}
          noFooter={hideFooter}
          hideLogo={isLoginRoute && isMobile}
        />
      )}
      {renderLogin()}
      {isMobile ? null : renderTTU()}
      {isMobile ? null : renderTTUWidget()}
      {showReferralDialog ? renderReferralPopUp() : null}
    </Container>
  );
};

export default Layout;
